import { useEffect, useMemo } from 'react'
import type { IndexDef, LevelCode, ObjectiveEvent, ObjectiveState } from '@/api/types/readiness'
import { levelNum } from '@/api/types/readiness'
import { allObjectives, domainColor, levelName } from '@/lib/indexes/registry'
import { needsConfirmation } from '@/lib/readiness/score'
import { movesSince, scopeGrowthFor } from '@/lib/readiness/progress'
import { RdModal } from '@/components/readiness/RdModal'
import { DomainDot, Icon } from '@/components/readiness/primitives'
import { CoverageChip } from '@/components/readiness/ProgressParts'
import { StepSparkline } from '@/components/readiness/StepSparkline'
import { OriginLink } from '@/lib/readiness/nav'
import { useReadinessStore } from '@/stores/readinessStore'
import { formatDate } from '@/components/common/format'

// ── Objectives moved · quick view (Iteration 5 §8) ──────────────────────────
// Canvas option B: a read-only 800px modal grouped by objective. Counting is
// the tile's: confirmed, declared and overridden level changes in the last
// 90 days from `readiness_objective_event`, coverage-driven drops counted as
// down and labelled. Each row links to the objective with origin Progress.

const DAY = 86_400_000
const UP = 'var(--rd-status-ok-text)'
const DOWN = 'var(--rd-status-warn)'

export function ObjectivesMovedModal({ index, events, states, orgName, orgId, indexId, theme, onClose, days = 90 }: { index: IndexDef; events: ObjectiveEvent[]; states: Record<string, ObjectiveState>; orgName: string; orgId: string; indexId: string; theme: 'light' | 'dark'; onClose: () => void; days?: number }) {
  const track = useReadinessStore((s) => s.track)
  useEffect(() => {
    track(orgId, indexId, { name: 'readiness.objectives_moved_opened' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const to = Date.now()
  const from = to - days * DAY
  const q = `?index=${indexId}`
  const moves = useMemo(() => movesSince(events, new Date(from).toISOString()), [events, from])
  const all = useMemo(() => [...moves.up, ...moves.down, ...moves.coverage].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at)), [moves])
  const items = useMemo(() => allObjectives(index), [index])
  // One row per objective, ordered by most recent move, newest first.
  const rows = useMemo(() => {
    const by = new Map<string, ObjectiveEvent[]>()
    all.forEach((e) => by.set(e.objective_code!, [...(by.get(e.objective_code!) ?? []), e]))
    return Array.from(by.entries())
      .map(([code, evs]) => {
        const found = items.find(({ objective }) => objective.code === code)
        const net = levelNum(evs[evs.length - 1].to_level!) - levelNum(evs[0].from_level!)
        return { code, evs, found, net, last: evs[evs.length - 1].occurred_at, now: evs[evs.length - 1].to_level! }
      })
      .filter((r) => r.found)
      .sort((a, b) => b.last.localeCompare(a.last))
  }, [all, items])
  const notCounted = items.filter(({ objective }) => needsConfirmation(states[objective.code])).map(({ objective }) => ({ code: objective.code, from: states[objective.code].status === 'suggested' ? 'L0' : states[objective.code].level, to: states[objective.code].suggested_level! }))
  const total = moves.up.length + moves.down.length + moves.coverage.length
  const actorOf = (e: ObjectiveEvent) => (e.event_type === 'confirmed' ? `confirmed by ${e.actor}` : e.event_type === 'overridden' ? `overridden by ${e.actor}` : `declared by ${e.actor}`)

  return (
    <RdModal
      title={`Objectives moved · last ${days} d`}
      subtitle={`${orgName || 'Organisation'} · ${formatDate(new Date(from).toISOString())} – ${formatDate(new Date(to).toISOString())} · ${total} confirmed ${total === 1 ? 'move' : 'moves'} across ${rows.length} ${rows.length === 1 ? 'objective' : 'objectives'}`}
      width={800}
      onClose={onClose}
      testId="objectives-moved-modal"
      bodyClassName="p-0"
      footerClassName="justify-between"
      footer={
        <>
          <p className="min-w-0 text-[12px]" style={{ color: 'var(--rd-text-muted)' }} data-testid="moved-not-counted">
            {notCounted.length > 0 && (
              <>Not counted: {notCounted.slice(0, 3).map((n, i) => <span key={n.code}>{i > 0 ? '; ' : ''}<span className="rd-mono rd-id">{n.code}</span> suggested {n.from} → {n.to}</span>)}{notCounted.length > 3 ? ` +${notCounted.length - 3} more` : ''}, awaiting confirmation.</>
            )}
          </p>
          <button type="button" className="rd-btn" onClick={onClose}>Close</button>
        </>
      }
    >
      <div className="grid gap-x-4 px-6 py-2 text-[12px] font-semibold" style={{ gridTemplateColumns: 'minmax(0,1fr) 190px 150px', background: 'var(--rd-bg-header)', color: 'var(--rd-text-muted)' }}>
        <span>Objective and moves</span><span>Last {days} d</span><span>Now</span>
      </div>
      {rows.length === 0 && <p className="px-6 py-8 text-center text-[13px]" style={{ color: 'var(--rd-text-muted)' }}>No objectives changed level in the last {days} days.</p>}
      <ul>
        {rows.map((r) => {
          const { domain, objective } = r.found!
          const color = domainColor(domain, theme)
          return (
            <li key={r.code} style={{ borderTop: '1px solid var(--rd-border-subtle)' }}>
              <OriginLink to={`/readiness/${r.code}${q}`} originKey="kpi-moved" className="rd-qv-row grid items-center gap-x-4 px-6 py-3 text-[13px]" style={{ gridTemplateColumns: 'minmax(0,1fr) 190px 150px', color: 'inherit', textDecoration: 'none' }} data-testid={`moved-row-${r.code}`}>
                <div className="min-w-0">
                  <p className="flex min-w-0 items-center gap-2">
                    <DomainDot domain={domain} theme={theme} size={10} />
                    <span className="rd-mono rd-id font-semibold">{r.code}</span>
                    <span className="min-w-0 flex-1 truncate font-semibold" title={objective.title}>{objective.title}</span>
                  </p>
                  <ul className="mt-1 space-y-0.5 text-[12px]">
                    {r.evs.map((e) => {
                      const up = levelNum(e.to_level!) > levelNum(e.from_level!)
                      const growth = e.cause === 'coverage' ? scopeGrowthFor(events, e) : null
                      return (
                        <li key={e.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className="rd-mono rd-id" style={{ color: 'var(--rd-text-muted)' }}>{formatDate(e.occurred_at)}</span>
                          <span className="rd-mono rd-id">{e.from_level} → {e.to_level} <span style={{ color: up ? UP : DOWN }}>{up ? '↑' : '↓'}</span></span>
                          {e.cause === 'coverage' ? <><CoverageChip inline />{growth != null && <span style={{ color: 'var(--rd-text-muted)' }}>+{growth.toLocaleString()} assets found</span>}</> : <span style={{ color: 'var(--rd-text-muted)' }}>{actorOf(e)}</span>}
                        </li>
                      )
                    })}
                  </ul>
                </div>
                <div><StepSparkline index={index} events={events} code={r.code} from={from} to={to} color={color} width={190} height={48} interactive={false} /></div>
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="rd-mono rd-id font-bold">L{levelNum(r.now)} {levelName(index, r.now)}</p>
                    <p className="text-[12px]" style={{ color: r.net > 0 ? UP : r.net < 0 ? DOWN : 'var(--rd-text-muted)' }}>{r.net > 0 ? `Net ↑ ${r.net} ${r.net === 1 ? 'level' : 'levels'}` : r.net < 0 ? `Net ↓ ${-r.net} ${-r.net === 1 ? 'level' : 'levels'}` : 'Net no change'}</p>
                  </div>
                  <Icon name="chevron-right" size={16} className="rd-qv-chevron shrink-0" style={{ color: 'var(--rd-text-faint)' }} />
                </div>
              </OriginLink>
            </li>
          )
        })}
      </ul>
    </RdModal>
  )
}

export type { LevelCode }
